import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useApi } from "../../../service/ApiContext";
import { useAlert } from "../../../components/AlertContextComponent";
import InterviewHubApiService from "../service/InterviewHubApiService";
import {
  GradeResumeSession,
  GradeResumeStatus,
  ResumeImprovement,
  ResumeScores,
} from "../data/interface/InterviewHubInterface";
import ResumePicker, { ResumeFileData } from "../components/ResumePicker";
import Loader from "../../../utils/Utils";
import "../styles/InterviewHubStyles.css";

// ─── Sub-component: Status Tooltip (Shared style) ────────────────────────────

const StatusTooltip: React.FC<{ message: string; isFailed?: boolean }> = ({
  message,
  isFailed,
}) => (
  <div
    className={`interview-hub-tooltip ${isFailed ? "interview-hub-tooltip-failed" : ""}`}
    style={{ bottom: "calc(100% + 5px)" }}
  >
    {message}
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = (dateString: string) => {
  if (!dateString) return "";
  const normalized = dateString.replace(/\//g, "-");
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return dateString;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}/${month}/${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const scoreColor = (pct: number) => {
  if (pct >= 75) return "#34d399";
  if (pct >= 50) return "#fbbf24";
  return "#f87171";
};

const POLL_INTERVAL_MS = 4000;
const CIRCUMFERENCE = 2 * Math.PI * 52;

// ─── Sub-component: Animated Score Ring ───────────────────────────────────────

const ScoreRing: React.FC<{ score: number; max?: number }> = ({
  score,
  max = 100,
}) => {
  const pct = Math.min((score / max) * 100, 100);
  const color = scoreColor(pct);
  const offset = CIRCUMFERENCE * (1 - pct / 100);

  return (
    <div className="rg-ring-container">
      <svg viewBox="0 0 120 120" className="rg-ring-svg">
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="rgba(255,255,255,0.07)"
          strokeWidth="10"
        />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
        />
      </svg>
      <div className="rg-ring-label">
        <span className="rg-ring-number" style={{ color }}>
          {score}
        </span>
        <span className="rg-ring-unit">/{max}</span>
      </div>
    </div>
  );
};

// ─── Sub-component: Score Bar ─────────────────────────────────────────────────

const ScoreBar: React.FC<{ label: string; value: number; max?: number }> = ({
  label,
  value,
  max = 10,
}) => {
  const pct = (value / max) * 100;
  const color = scoreColor(pct);

  return (
    <div className="rg-score-bar-wrapper">
      <div className="rg-score-bar-header">
        <span className="rg-score-bar-label">{label}</span>
        <span className="rg-score-bar-value" style={{ color }}>
          {value}/{max}
        </span>
      </div>
      <div className="rg-score-bar-track">
        <div
          className="rg-score-bar-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

// ─── Sub-component: Score Card ────────────────────────────────────────────────

const ScoreCard: React.FC<{ resumeScore: ResumeScores }> = ({
  resumeScore,
}) => (
  <div className="rg-score-card">
    <ScoreRing score={resumeScore.total} max={100} />
    <div className="rg-score-bars">
      <ScoreBar label="Readability" value={resumeScore.readability} />
      <ScoreBar label="Impact" value={resumeScore.impact} />
      <ScoreBar label="Conciseness" value={resumeScore.conciseness} />
      <ScoreBar label="Tech Clarity" value={resumeScore.tech_clarity} />
    </div>
    {resumeScore.actionable_improvements?.length > 0 && (
      <div className="rg-tips-box">
        <div className="rg-tips-title">💡 Quick Wins</div>
        <ul className="rg-tips-list">
          {resumeScore.actionable_improvements.map((tip, i) => (
            <li key={i} className="rg-tip-item">
              {tip}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// ─── Sub-component: Improvements List ────────────────────────────────────────

const ICONS = ["🚀", "💡", "🎯", "🔑", "⭐", "✨", "🔥", "📌"];

const ImprovementsList: React.FC<{ improvements: ResumeImprovement[] }> = ({
  improvements,
}) => {
  if (!improvements || improvements.length === 0) {
    return (
      <div className="rg-empty-improvements">
        <span>🎉</span>
        <p>No major improvements needed — great work!</p>
      </div>
    );
  }

  return (
    <div className="rg-improvements-container">
      {improvements.map((imp, i) => (
        <div key={i} className="rg-improvement-item">
          <div className="rg-improvement-row">
            <div className="rg-improvement-icon">{ICONS[i % ICONS.length]}</div>
            <div className="rg-improvement-content">
              <div className="rg-improvement-line">
                <strong className="rg-label-original">Original:</strong>
                <span className="rg-text-original">{imp.original}</span>
              </div>
              <div className="rg-improvement-line">
                <strong className="rg-label-improved">Improved:</strong>
                <span className="rg-text-improved">{imp.improvement}</span>
              </div>
              <div className="rg-improvement-reason">Reason: {imp.reason}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Sub-component: Polling Screen ───────────────────────────────────────────

const PollingScreen: React.FC = () => (
  <div className="rg-polling-screen">
    <div className="rg-polling-orb">
      <div className="rg-polling-spinner" />
      <span className="rg-polling-icon">📄</span>
    </div>
    <h3 className="rg-polling-title">Analysing Your Resume</h3>
    <p className="rg-polling-desc">
      JozAI is grading your resume across readability, impact, conciseness, and
      tech clarity. This usually takes 30–60 seconds.
    </p>
    <div className="rg-polling-dots">
      <span />
      <span />
      <span />
    </div>
  </div>
);

// ─── Sub-component: Result Dashboard ─────────────────────────────────────────

type ResultTab = "score" | "improvements";

const ResultDashboard: React.FC<{
  session: GradeResumeSession;
  onBack: () => void;
}> = ({ session, onBack }) => {
  const [activeTab, setActiveTab] = useState<ResultTab>("score");

  const impCount = session.resume_improvement?.length ?? 0;

  return (
    <div className="rg-result-dashboard">
      {/* Header */}
      <div className="rg-result-header">
        <button className="rg-back-btn" onClick={onBack}>
          ← Back
        </button>
        <div className="rg-result-title-block">
          <h2 className="rg-result-title">Resume Grade Report</h2>
          <p className="rg-result-subtitle">
            {session.session_name || "Grade Session"} &nbsp;·&nbsp;{" "}
            {formatDateTime(session.created_at)}
          </p>
        </div>
        <div
          className="rg-result-badge"
          style={{ color: scoreColor(session.resume_score?.total ?? 0) }}
        >
          {session.resume_score?.total ?? "–"}
          <span>/100</span>
        </div>
      </div>

      {/* Tab switcher - Hidden on failure */}
      {session.status === GradeResumeStatus.COMPLETED && (
        <div className="rg-result-tabs">
          <button
            className={`rg-result-tab ${activeTab === "score" ? "rg-result-tab-active" : ""}`}
            onClick={() => setActiveTab("score")}
          >
            📊 Score Breakdown
          </button>
          <button
            className={`rg-result-tab ${activeTab === "improvements" ? "rg-result-tab-active" : ""}`}
            onClick={() => setActiveTab("improvements")}
          >
            💡 Improvements
            {impCount > 0 && <span className="rg-tab-badge">{impCount}</span>}
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className="rg-result-tab-content">
        <div className="rg-result-inner-container">
          {session.status === GradeResumeStatus.FAILED && (
            <div className="rg-failure-state">
              <div className="rg-failure-icon">⚠️</div>
              <h3 className="rg-failure-title">Grading Failed</h3>
              <p className="rg-failure-reason">
                {session.reason ||
                  "We encountered an error while analyzing your resume. Please try again."}
              </p>
            </div>
          )}
          {session.status === GradeResumeStatus.COMPLETED &&
            activeTab === "score" &&
            session.resume_score && (
              <ScoreCard resumeScore={session.resume_score} />
            )}
          {session.status === GradeResumeStatus.COMPLETED &&
            activeTab === "improvements" && (
              <ImprovementsList
                improvements={session.resume_improvement ?? []}
              />
            )}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-component: Session Card ─────────────────────────────────────────────

const GradeSessionCard: React.FC<{
  session: GradeResumeSession;
  onViewReason: (session: GradeResumeSession) => void;
  onAction: (session: GradeResumeSession) => void;
}> = ({ session, onViewReason, onAction }) => {
  const isCompleted = session.status === GradeResumeStatus.COMPLETED;
  const isFailed = session.status === GradeResumeStatus.FAILED;
  const isPending = session.status === GradeResumeStatus.INITIATED;

  const statusClass = isCompleted
    ? "interview-hub-status-scored"
    : isFailed
      ? "interview-hub-status-failed"
      : "interview-hub-status-pending";

  const statusLabel = isCompleted
    ? "✅ Graded"
    : isFailed
      ? "❌ Failed"
      : "🕐 Processing";

  const actionLabel = isCompleted
    ? "View Results"
    : isFailed
      ? "View Reason"
      : "Processing…";

  // Get score-based styling
  const getScoreStyles = () => {
    if (!isCompleted || typeof session.resume_score?.total !== "number") {
      return {};
    }

    const score = session.resume_score.total;

    if (score >= 81 && score <= 100) {
      return {
        backgroundColor: "#E3FFF5",
        border: "1px solid #24C782",
      };
    } else if (score >= 51 && score <= 80) {
      return {
        backgroundColor: "#FFF8DB",
        border: "1px solid #FFCA64",
      };
    } else if (score >= 0 && score <= 50) {
      return {
        backgroundColor: "#FFEEEE",
        border: "1px solid #E84C3D",
      };
    }

    return {};
  };

  const truncateFileName = (name: string, maxLength: number = 25) => {
    if (!name) return "Resume Analysis";
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength - 3) + "...";
  };

  return (
    <div className="rg-session-card" style={getScoreStyles()}>
      {/* Left column: file name and date */}
      <div className="rg-card-left">
        <div className="rg-card-title" title={session.session_name}>
          {truncateFileName(session.session_name || "Resume Analysis")}
        </div>
        <div className="rg-card-date">
          Analyzed: {formatDateTime(session.created_at)}
        </div>
      </div>

      {/* Right column: percentage and button on the same line */}
      <div className="rg-card-right">
        {isCompleted && typeof session.resume_score?.total === "number" && (
          <div className="rg-card-score">{session.resume_score.total}%</div>
        )}

        <button
          className={`interview-hub-action-btn ${isPending ? "interview-hub-action-btn-muted" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (isFailed || isPending) {
              onViewReason(session);
            } else {
              onAction(session);
            }
          }}
          disabled={isPending}
        >
          {actionLabel}
        </button>
      </div>

      {/* Status badge at bottom */}
      <div className="rg-card-status">
        <span className={`interview-hub-status ${statusClass}`}>
          {statusLabel}
        </span>
      </div>
    </div>
  );
};
// ─── Main Component: ResumeGradingTab ────────────────────────────────────────

type ViewState = "list" | "polling" | "result";

const ResumeGradingTab: React.FC<{
  onToggleTabs: (show: boolean) => void;
  showUploadModal?: boolean;
  setShowUploadModal?: (show: boolean) => void;
}> = ({ onToggleTabs, showUploadModal = false, setShowUploadModal }) => {
  const api = useApi();
  const { showAlert } = useAlert();
  const service = useMemo(() => new InterviewHubApiService(api), [api]);

  const [view, setView] = useState<ViewState>("list");
  const [sessions, setSessions] = useState<GradeResumeSession[]>([]);
  const [selectedFile, setSelectedFile] = useState<ResumeFileData | null>(null);
  const [resultSession, setResultSession] = useState<GradeResumeSession | null>(
    null,
  );
  const [reasonModalSession, setReasonModalSession] =
    useState<GradeResumeSession | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Tab visibility management ──
  useEffect(() => {
    if (view === "polling" || view === "result") {
      onToggleTabs(false);
    } else {
      onToggleTabs(true);
    }
    return () => onToggleTabs(true);
  }, [view, onToggleTabs]);

  // ── Fetch session list on mount ──
  useEffect(() => {
    fetchSessions();
    return () => stopPolling();
  }, []);

  // Reset file selection when modal closes
  useEffect(() => {
    if (!showUploadModal) {
      setSelectedFile(null);
    }
  }, [showUploadModal]);

  const fetchSessions = async () => {
    try {
      Loader.showLoading();
      const data = await service.listGradeSessions();
      setSessions(data.grade_sessions || []);
    } catch (err) {
      console.error("Failed to fetch grade sessions", err);
    } finally {
      Loader.dismissLoading();
    }
  };

  // ── Polling logic ──
  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback(
    (sessionId: string) => {
      stopPolling();

      const poll = async () => {
        try {
          const res = await service.getResumeGradeResult(sessionId);
          if ("resume_grade" in res) {
            stopPolling();
            setResultSession(res.resume_grade);
            setView("result");
            fetchSessions();
          } else if ("status" in res && res.status === "Failed") {
            stopPolling();
            setResultSession({
              session_id: sessionId,
              status: GradeResumeStatus.FAILED,
              reason:
                (res as any).reason || "Grading failed. Please try again.",
              created_at: new Date().toISOString(),
              last_updated: new Date().toISOString(),
              candidate_id: "",
              email_address: "",
            });
            setView("result");
            fetchSessions();
          } else {
            pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        } catch (err) {
          console.error("Polling error:", err);
          pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      };

      pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [service],
  );

  // ── Upload & Start ──
  const handleUploadAndGrade = async () => {
    if (!selectedFile) return;
    Loader.showLoading("Uploading resume…");
    try {
      const uploadUrlData = await api.get<{
        uploadURL: string;
        key: string;
        downloadURL: string;
      }>(
        `/create_upload_url?filename=${encodeURIComponent(selectedFile.name)}`,
      );
      const { uploadURL, downloadURL } = uploadUrlData.data;

      await axios.put(uploadURL, selectedFile.binary, {
        headers: { "Content-Type": selectedFile.type },
      });

      const startRes = await service.startResumeGrading({
        file_url: downloadURL,
      });

      // Close modal and start polling
      setShowUploadModal?.(false);
      setView("polling");
      startPolling(startRes.session_id);
    } catch (err: any) {
      console.error("Failed to start resume grading", err);
      const data = err?.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        "Something went wrong while starting the grading. Please try again.";
      showAlert({
        show: true,
        title: "Error",
        message: msg,
        alertType: "error",
        onlyNeedOneButton: true,
        button1Text: "Got it",
        btn1onPressed() {},
      });
    } finally {
      Loader.dismissLoading();
    }
  };

  // ── View a past session ──
  const handleViewSession = async (session: GradeResumeSession) => {
    if (session.status === GradeResumeStatus.COMPLETED) {
      if (session.resume_score) {
        setResultSession(session);
        setView("result");
      } else {
        try {
          const res = await service.getResumeGradeResult(session.session_id);
          if ("resume_grade" in res) {
            setResultSession(res.resume_grade);
            setView("result");
          }
        } catch (err) {
          console.error("Failed to fetch grade result", err);
        }
      }
    } else if (session.status === GradeResumeStatus.FAILED) {
      setResultSession(session);
      setView("result");
    }
  };

  // ── Render ──
  if (view === "polling") {
    return <PollingScreen />;
  }

  if (view === "result" && resultSession) {
    return (
      <ResultDashboard
        session={resultSession}
        onBack={() => {
          setResultSession(null);
          setView("list");
        }}
      />
    );
  }

  // "list" view - No button here, button is in parent header
  return (
    <div className="rg-list-view">
      {/* Past sessions */}
      {sessions.length > 0 ? (
        <>
          <h3 className="rg-section-title">Previous Grading Sessions</h3>
          <div className="rg-sessions-grid">
            {sessions.map((s) => (
              <GradeSessionCard
                key={s.session_id}
                session={s}
                onViewReason={(session) => setReasonModalSession(session)}
                onAction={handleViewSession}
              />
            ))}
          </div>
        </>
      ) : (
        <div
          className="interview-hub-empty-state"
          style={{ marginTop: "2rem" }}
        >
          <h3>No Grading Sessions Yet</h3>
          <p style={{ textAlign: "center" }}>
            Upload your resume to get an instant AI-powered grade and
            improvement suggestions.
          </p>
        </div>
      )}

      {/* Upload Modal - Controlled by parent */}
      {showUploadModal && (
        <div
          className="rg-modal-overlay"
          onClick={() => setShowUploadModal?.(false)}
        >
          <div className="rg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rg-modal-header">
              <h2 className="rg-modal-title">Upload Resume for Grading</h2>
              <button
                className="rg-modal-close"
                onClick={() => {
                  setShowUploadModal?.(false);
                  setSelectedFile(null);
                }}
              >
                ✕
              </button>
            </div>
            <p className="rg-modal-desc">
              JozAI will analyse your resume and score it across 4 key
              dimensions with actionable improvements.
            </p>
            <ResumePicker file={selectedFile} onChange={setSelectedFile} />
            <div className="rg-modal-footer">
              <button
                className="rg-modal-cancel-btn"
                onClick={() => {
                  setShowUploadModal?.(false);
                  setSelectedFile(null);
                }}
              >
                Cancel
              </button>
              <button
                className="interview-hub-start-btn"
                disabled={!selectedFile}
                onClick={handleUploadAndGrade}
              >
                Analyse Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reason Popup Modal */}
      {reasonModalSession && (
        <div
          className="rg-modal-overlay"
          onClick={() => setReasonModalSession(null)}
        >
          <div className="rg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rg-modal-header">
              <h2 className="rg-modal-title">
                {reasonModalSession.status === GradeResumeStatus.FAILED
                  ? "Grading Failed"
                  : "Session Status"}
              </h2>
              <button
                className="rg-modal-close"
                onClick={() => setReasonModalSession(null)}
              >
                ✕
              </button>
            </div>
            <p
              className="rg-modal-desc"
              style={{ fontSize: "1rem", color: "#e2e8f0", margin: "1rem 0" }}
            >
              {reasonModalSession.status === GradeResumeStatus.FAILED
                ? reasonModalSession.reason ||
                  "An error occurred while analyzing your resume. Please try a different file."
                : "We are currently processing your resume. Your AI-powered grade and improvement suggestions will appear here shortly."}
            </p>
            <div className="rg-modal-footer">
              <button
                className="interview-hub-start-btn"
                style={{ width: "100%" }}
                onClick={() => setReasonModalSession(null)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeGradingTab;
